import React from 'react';

// Helper function to handle conditional display logic
// --- REPLACE the old shouldDisplay with this ---
const shouldDisplay = (config, values) => {
    if (!config || !Array.isArray(config.condition)) return true;
    const condition = config.condition;

    const normalize = (v) => (typeof v === 'string' ? v.toLowerCase() : v);

    const evalSingle = (arr) => {
        const [key, expected, op] = arr;
        const actual = values ? values[key] : undefined;
        const a = normalize(actual);
        const b = normalize(expected);

        if (op === 'eq') return a === b;
        if (op === 'neq') return a !== b;
        if (op === 'notNull') return actual !== '' && actual !== null && actual !== undefined;
        if (op === 'gt') return parseFloat(a) > parseFloat(b);
        if (op === 'gte') return parseFloat(a) >= parseFloat(b);
        if (op === 'lte') return parseFloat(a) <= parseFloat(b);
        return false;
    };

    if (condition.length <= 3) {
        return evalSingle(condition);
    }

    const cond1 = condition.slice(0, 3);
    const cond2 = condition.slice(3, 6);
    const logicalOp = condition[6];

    const r1 = evalSingle(cond1);
    const r2 = evalSingle(cond2);

    return logicalOp === '&&' ? (r1 && r2) : (r1 || r2);
};

// Component for rendering a standard input field
const renderInputField = (field, formik) => (
    <input
        key={field.name}
        className="form-control input-underline kyc-summary-input"
        type={field.type || 'text'}
        id={field.name}
        name={field.name}
        placeholder={field.placeholder}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[field.name] || ""}
        readOnly={field.name === 'emp_city' || field.name === 'emp_state'}
    />
);

// Component for rendering radio buttons (with special style for employment_type and gender)
const renderRadioField = (field, formik) => {
    // Use button style for employment_type and gender
    if (field.name === 'employment_type') {
        return (
            <div key={field.name} className="d-flex gap-2 kyc-genderbg-btn">
                {field.options.map((option, idx) => (
                    <React.Fragment key={idx}>
                        <input
                            type="radio"
                            className="btn-check"
                            name={field.name}
                            id={`${field.name}-${option.value}`}
                            value={option.value}
                            onChange={formik.handleChange}
                            checked={formik.values[field.name] === option.value}
                            onBlur={formik.handleBlur}
                        />
                        <label className="btn btn-outline-primary kyc-custom-gender-btn mb-0 fs-16" htmlFor={`${field.name}-${option.value}`}>{option.label}</label>
                    </React.Fragment>
                ))}
            </div>
        );
    }
    // Default radio style
    return (
        <div key={field.name} className="radio-group d-flex gap-4">
            {field.options.map((option, idx) => (
                <div className="form-check" key={idx}>
                    <input
                        className="form-check-input"
                        type="radio"
                        name={field.name}
                        id={`${field.name}-${option.value}`}
                        value={option.value}
                        checked={formik.values[field.name] === option.value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    />
                    <label className="form-check-label" htmlFor={`${field.name}-${option.value}`}>
                        {option.label}
                    </label>
                </div>
            ))}
        </div>
    );
};

// Component for rendering select dropdowns
const renderSelectField = (field, formik) => (
    <select
        key={field.name}
        className="form-select input-underline kyc-summary-dropdown"
        id={field.name}
        name={field.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values[field.name]}
    >
        <option value="">{field.placeholder}</option>
        {field.options?.map((option, idx) => (
            <option key={idx} value={option.value}>
                {option.label}
            </option>
        ))}
    </select>
);

// Proper React component for search-inputs that use static options (e.g. designation).
// Uses useState so the dropdown can be properly opened and closed.
const StaticSearchInput = ({ field, formik }) => {
    const { useState: useLocalState } = React;
    const [open, setOpen] = useLocalState(false);

    const rawValue = formik.values[field.name] || '';
    const staticOptions = field.options || [];
    const filtered = rawValue.length > 0
        ? staticOptions.filter(o => o.label.toLowerCase().includes(rawValue.toLowerCase()))
        : staticOptions; // show all when nothing typed yet

    const handleSelect = (value) => {
        formik.setFieldValue(field.name, value);
        setOpen(false);
    };

    return (
        <div className="employer-search-container w-100" >
            <input
                type="text"
                className="form-control input-underline kyc-summary-input"
                id={field.name}
                name={field.name}
                placeholder={field.placeholder}
                onChange={(e) => {
                    formik.setFieldValue(field.name, e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={(e) => {
                    formik.handleBlur(e);
                    // Delay close so onClick on list items fires first
                    setTimeout(() => setOpen(false), 150);
                }}
                value={rawValue}
                autoComplete="off"
            />
            {open && filtered.length > 0 && (
                <ul
                    className="employer-suggestions-list position-absolute list-unstyled bg-white border border-secondary w-100 p-2 rounded-2"
                    style={{ zIndex: 999, maxHeight: '200px', overflowY: 'auto' }}
                >
                    {filtered.map((option, idx) => (
                        <li
                            key={idx}
                            onMouseDown={(e) => { e.preventDefault(); handleSelect(option.value); }}
                            className="p-2 cursor-pointer border-bottom"
                            style={{ cursor: 'pointer' }}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// Component for rendering a search input with suggestions
const renderSearchInputField = (field, formik, dynamicProps) => {
    const { handleEmployerSearchChange, handleEmployerSelect, employerSearchResults, showEmployerSuggestions } = dynamicProps;

    // Non-employer search-inputs (e.g. designation) use static options with proper open/close state
    if (field.name !== 'employer_name') {
        return <StaticSearchInput key={field.name} field={field} formik={formik} />;
    }

    // employer_name: live API search via dynamicProps
    return (
        <div key={field.name} className="employer-search-container w-100">
            <input
                type="text"
                className="form-control input-underline kyc-summary-input"
                id={field.name}
                name={field.name}
                placeholder={field.placeholder}
                onChange={handleEmployerSearchChange}
                onBlur={formik.handleBlur}
                value={formik.values[field.name]}
                autoComplete="off"
            />
            {showEmployerSuggestions && employerSearchResults.length > 0 && (
                <ul className="employer-suggestions-list position-absolute list-unstyled bg-white border border-secondary w-100 p-2 rounded-2">
                    {employerSearchResults.map((result, idx) => (
                        <li
                            key={idx}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleEmployerSelect(result)
                                }
                            }}
                            onClick={() => handleEmployerSelect(result)}
                            className="p-2 cursor-pointer border-bottom"
                        >
                            {result}
                        </li>
                    ))}
                    {field.fallbackOptions && field.fallbackOptions.map((option, idx) => (
                        <li
                            key={`fallback-${idx}`}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleEmployerSelect(option.name)
                                }
                            }}
                            onClick={() => handleEmployerSelect(option.name)}
                            className="p-2 cursor-pointer"
                        >
                            {option.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// Component for rendering a conditional select field (options depend on another field's value)
const renderConditionalSelectField = (field, formik) => {
    const { conditionalOptions } = field;
    if (!conditionalOptions) return null;

    const { conditions, options } = conditionalOptions;
    const values = formik.values;

    // Find the first matching condition key
    let resolvedKey = null;
    for (const cond of conditions) {
        const [key, expected, op] = cond.condition;
        const actual = values?.[key];

        let matched = false;
        if (op === 'eq') {
            matched = (typeof actual === 'string' ? actual.toLowerCase() : actual) ===
                (typeof expected === 'string' ? expected.toLowerCase() : expected);
        } else if (op === 'in') {
            matched = Array.isArray(expected) && expected.includes(actual);
        } else if (op === 'neq') {
            matched = actual !== expected;
        }

        // Handle compound conditions (e.g. && with second key/value/op)
        if (matched && cond.condition.length > 3) {
            const [key2, expected2, op2, logicalOp] = cond.condition.slice(3); // ✅ fixed
            const actual2 = values?.[key2];
            let matched2 = false;
            if (op2 === 'eq') matched2 = actual2 === expected2;
            else if (op2 === 'in') matched2 = Array.isArray(expected2) && expected2.includes(actual2);
            matched = logicalOp === '&&' ? (matched && matched2) : (matched || matched2);
        }

        if (matched && cond.value) {
            resolvedKey = cond.value;
            break;
        }
    }

    const resolvedOptions = resolvedKey ? (options[resolvedKey] || []) : [];

    return (
        <select
            key={field.name}
            className="form-select input-underline kyc-summary-dropdown"
            id={field.name}
            name={field.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values[field.name] || ''}
        >
            <option value="">{field.placeholder}</option>
            {resolvedOptions.map((option, idx) => (
                <option key={idx} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
};

// Component for rendering a group of select fields
const renderGroupField = (field, formik) => (
    <div key={field.name} className="d-flex gap-3 theme-input col-12">
        {/* Years with current employer */}
        <div className="col-5">
            <label htmlFor={field.fields[0].name} className="emi-label gender-label">{field.fields[0].label}</label>
            <select
                className="form-select input-underline kyc-summary-dropdown"
                id={field.fields[0].name}
                name={field.fields[0].name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values[field.fields[0].name]}
            >
                <option value="">{field.fields[0].placeholder}</option>
                {field.fields[0].options?.map((option, idx) => (
                    <option key={idx} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {formik.touched[field.fields[0].name] && formik.errors[field.fields[0].name] ? (
                <div className="text-danger small">{formik.errors[field.fields[0].name]}</div>
            ) : null}
        </div>
        {/* Spacer */}
        <div className="col-1"></div>
        {/* Months with current employer */}
        <div className="col-5 pe-1">
            <label htmlFor={field.fields[1].name} className="emi-label gender-label">{field.fields[1].label}</label>
            <select
                className="form-select input-underline kyc-summary-dropdown"
                id={field.fields[1].name}
                name={field.fields[1].name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values[field.fields[1].name]}
            >
                <option value="">{field.fields[1].placeholder}</option>
                {field.fields[1].options?.map((option, idx) => (
                    <option key={idx} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {console.log(formik.touched?.[field.fields[1].name], field.fields[1].name, formik.errors?.[field.fields[1].name], formik.values?.[field.fields[1].name])}
            {formik.touched[field.fields[1].name] && formik.errors[field.fields[1].name] ? (
                <div className="text-danger small">{formik.errors[field.fields[1].name]}</div>
            ) : null}
        </div>
    </div>
);

const renderPincodeField = (field, formik) => (
    <input
        key={field.name}
        className="form-control input-underline kyc-summary-input"
        type="text"
        id={field.name}
        name={field.name}
        placeholder={field.placeholder || ' '}
        value={formik.values[field.name]}
        maxLength={6} // Standard pincode length
        onChange={(e) => {
            const val = e.target.value;
            // Only allow numbers and limit to 6 digits
            const cleanVal = val.replace(/[^0-9]/g, '').slice(0, 6);
            formik.setFieldValue(field.name, cleanVal);

            // If you have a pincode search function in your summary, 
            // you can trigger it here after 6 digits are entered
            if (cleanVal.length === 6 && field.onPincodeChange) {
                field.onPincodeChange(cleanVal);
            }
        }}
        onBlur={formik.handleBlur}
        disabled={field.disabled}
    />
);

// Main function to render all form elements
export const renderFormElements = (fields, formik, dynamicProps) => {

    // Helper to render a field with label, input, and error
    // List of fields that require the 'gender-label' class
    const genderLabelFields = [
        'employment_type',
        'employment_profile',
        'employer_name',
        'other_employer_name',
        'business_profile',
        'industry_name',
        'industry_type',
        'industry_list',
        'rbi_sector_category',
        'designation',
        'business_designation',
        'total_monthly_income',
        'total_family_income',
        'total_earning_members',
        'business_years',
        'business_months',
        'educational_qualification',
    ];

    const renderFieldWithLabel = (field, inputComponent) => {
    let isGenderLabel = genderLabelFields.includes(field.name);
    if (field.element === 'group' && field.fields) {
        // renderGroupField already renders its own per-subfield errors inline,
        // so just return the input component — don't render errors again here.
        return <>{inputComponent}</>;
    }
    return (
        <>
            <label className={`label form-label emi-label${isGenderLabel ? ' gender-label' : ''}`} htmlFor={field.name}>{field.label}</label>
            {inputComponent}
            {formik.touched[field.name] && formik.errors[field.name] ? (
                <div className="text-danger small">{formik.errors[field.name]}</div>
            ) : null}
        </>
    );
};

    // Prepare visible fields only
    const visibleFields = fields.filter(field => shouldDisplay(field.displayCondition, formik.values));

    // Group fields for rendering
    const rows = [];
    let col6Buffer = [];
    let employmentTypeRowAdded = false;
    let floatingRowCount = 0;
    const forceCol12 = [
        'designation',
        'business_designation',
        'total_monthly_income',
        'total_family_income',
        'total_earning_members',
        'employment_profile',
        'employer_name',
        'other_employer_name',
        'business_profile',
        'industry_name',
        'industry_type',
        'industry_list',
        'rbi_sector_category',
    ];
    const forceCol12FloatingBottom = [
        'emp_address_line_1',
        'emp_address_line_2',
        'emp_address_line_3',
        'emp_landmark',
        'emp_pincode',
        'emp_city',
        'emp_state',
    ];

    visibleFields.forEach((field, idx) => {
        let inputComponent;
        switch (field.element) {
            case 'input':
                inputComponent = renderInputField(field, formik);
                break;
            case 'pincode': // New Case
                inputComponent = renderPincodeField(field, formik);
                break;
            case 'radio':
                inputComponent = renderRadioField(field, formik);
                break;
            case 'select-modal':
            case 'plain-select-modal':
                inputComponent = renderSelectField(field, formik);
                break;
            case 'condition-select-modal':
                inputComponent = renderConditionalSelectField(field, formik);
                break;
            case 'search-input':
                inputComponent = renderSearchInputField(field, formik, dynamicProps);
                break;
            case 'group':
                inputComponent = renderGroupField(field, formik);
                break;
            default:
                inputComponent = null;
        }

        // Determine column class
        let wrapperClass = field.wrapperClass || '';
        // For the 4 occupational fields, always use col-12 ps-1 pe-1
        if (forceCol12.includes(field.name)) {
            wrapperClass = 'col-12 col-md-12 ps-1 pe-1';
        } else if (forceCol12FloatingBottom.includes(field.name)) {
            wrapperClass = 'col-12 col-md-12 ps-1 pe-1';
        } else if (field.element === 'group' && field.name === 'experience') {
            wrapperClass = 'ps-0 pe-0';
        }

        // Special case: Employment Type first row
        if (!employmentTypeRowAdded && field.name === 'employment_type') {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row" key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            rows.push(
                <div className="row floating-bottom mt-4 mx-auto" key={`row-employment-type`}>
                    <div className={wrapperClass + ' ps-1 pe-1'}>
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
            employmentTypeRowAdded = true;
            return;
        }

        // For Employment Profile, use floating-top; for Employer Name, force floating-bottom
        if (field.name === 'employment_profile') {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row mx-auto" key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            rows.push(
                <div className="row mx-auto floating-top" key={`row-occupational-employment_profile`}>
                    <div className="col-12 ps-1 pe-1">
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
            return;
        }
        if (field.name === 'educational_qualification') {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row mx-auto" key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            rows.push(
                <div className="row mx-auto floating-top" key={`row-education_qualification`}>
                    <div className="col-md-6 ps-1 pe-1">
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
            return;
        }
        if (field.name === 'employer_name') {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row mx-auto" key={`row-col6-${idx}`}>
                        {col6Buffer}
                    </div>
                );
                col6Buffer = [];
            }
            // Custom rendering for employer_name as per requested format
            rows.push(
                <div className="row mx-auto floating-bottom" key={`row-occupational-employer_name`}>
                    <div className="col-12 ps-1 pe-1">
                        {renderFieldWithLabel(
                            field,
                            renderSearchInputField(field, formik, dynamicProps)
                        )}
                    </div>
                </div>
            );
            return;
        }
        if (["business_profile", "industry_name", "industry_type", "industry_list", "rbi_sector_category", "designation", "business_designation", "other_employer_name"].includes(field.name)) {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row mx-auto" key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            rows.push(
                <div className="row mx-auto floating-bottom" key={`row-occupational-${field.name || idx}`}>
                    <div className="col-12 ps-1 pe-1">
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
            return;
        }

        // If col-12, flush col-6 buffer and wrap in row
        if (wrapperClass.includes('col-md-12') || wrapperClass.includes('col-12') || forceCol12FloatingBottom.includes(field.name)) {
            if (col6Buffer.length > 0) {
                rows.push(
                    <div className="row mx-auto" key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            let rowClass = "row mx-auto";
            // floating-bottom for the address/education fields
            if (forceCol12FloatingBottom.includes(field.name)) {
                rowClass = "row floating-bottom mx-auto";
            } else if (floatingRowCount < 5) {
                rowClass += " floating-bottom";
                floatingRowCount++;
            }
            // Exclude mx-auto for experience group
            if (field.element === 'group' && field.name === 'experience') {
                rowClass = rowClass.replace('mx-auto', '').trim();
            }
            rows.push(
                <div className={rowClass} key={`row-col12-${field.name || idx}`}>
                    <div className={wrapperClass}>
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
            return;
        } else if (wrapperClass.includes('col-md-6') || wrapperClass.includes('col-6')) {
            col6Buffer.push(
                <div className={wrapperClass} key={field.name || idx}>
                    {renderFieldWithLabel(field, inputComponent)}
                </div>
            );
            if (col6Buffer.length === 2) {
                let rowClass = "row mx-auto";
                if (floatingRowCount < 5) {
                    rowClass += " floating-bottom";
                    floatingRowCount++;
                }
                rows.push(
                    <div className={rowClass} key={`row-col6pair-${field.name || idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
        } else {
            // Fallback: treat as col-12
            if (col6Buffer.length > 0) {
                let rowClass = "row mx-auto";
                if (floatingRowCount < 5) {
                    rowClass += " floating-bottom";
                    floatingRowCount++;
                }
                rows.push(
                    <div className={rowClass} key={`row-col6-${idx}`}>{col6Buffer}</div>
                );
                col6Buffer = [];
            }
            let rowClass = "row mx-auto";
            if (floatingRowCount < 5) {
                rowClass += " floating-bottom";
                floatingRowCount++;
            }
            rows.push(
                <div className={rowClass} key={`row-fallback-${field.name || idx}`}>
                    <div className="col-12 ps-1 pe-1">
                        {renderFieldWithLabel(field, inputComponent)}
                    </div>
                </div>
            );
        }
    });
    // Flush any remaining col-6 fields
    if (col6Buffer.length > 0) {
        rows.push(
            <div className="row mx-auto" key={`row-col6-last`}>{col6Buffer}</div>
        );
    }

    return rows;
};

export default renderFormElements;